import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import { FaSpinner } from "react-icons/fa";
import { isValidEmail, normalizeEmail } from "../../util/email";
import { buildBrokerPath, useBrokerBasePath } from "../../components/Broker/brokerPaths";
import BrokerAlert from "../../components/Broker/BrokerAlert";
import BrokerHelpTooltip from "../../components/Broker/BrokerHelpTooltip";
import { DialogContext } from "../../store/context/DialogContext";
import { BrokerService } from "./BrokerService";
import {
  BROKER_SHARED_PRE_CREATE_FIELDS,
  getBrokerProduct,
  getEnabledBrokerProducts,
} from "./brokerProductRegistry";

// Per-field help copy, keyed by field name (looked up by the generic field renderer).
const FIELD_HELP = {
  brokerReferenceId: "Your own internal reference for this client (e.g. your CRM id). It appears on the applications list so you can find this client later.",
  Business_Name: "The client's legal or business name, exactly as it should appear on the FV Bank application.",
  firstName: "The client's legal first name, as it should appear on the FV Bank application.",
  middleName: "The client's legal middle name, if any.",
  lastName: "The client's legal last name, as it should appear on the FV Bank application.",
  email: "Primary contact email for this client. Application updates and individual verification links are tied to this address.",
};

const trimValue = (value) => String(value || "").trim();

const ENABLED_PRODUCTS = getEnabledBrokerProducts();
const SHARED_REFERENCE_FIELD = BROKER_SHARED_PRE_CREATE_FIELDS.find((field) => field.name === "brokerReferenceId");
const SHARED_EMAIL_FIELD = BROKER_SHARED_PRE_CREATE_FIELDS.find((field) => field.name === "email");

// Ordered field list for a product: reference id → product-specific fields → email.
const fieldsForProduct = (product) => [
  SHARED_REFERENCE_FIELD,
  ...(product?.preCreateFields || []),
  SHARED_EMAIL_FIELD,
].filter(Boolean);

const valueForField = (values, field) =>
  field.name === "email" ? normalizeEmail(values[field.name]) : trimValue(values[field.name]);

const validate = (values) => {
  const errors = {};
  const product = getBrokerProduct(values.accountType);
  if (!product) {
    errors.accountType = "Select a product.";
    return errors;
  }
  fieldsForProduct(product).forEach((field) => {
    const value = valueForField(values, field);
    if (field.required && !value) {
      errors[field.name] = `${field.label} is required.`;
    } else if (field.name === "email" && value && !isValidEmail(value)) {
      errors.email = "Enter a valid email.";
    }
  });
  return errors;
};

const getErrorMessage = (error, fallback = "Unable to create application") => {
  if (typeof error === "string" && error.trim()) return error;
  if (typeof error?.message === "string" && error.message.trim()) {
    try {
      const parsed = JSON.parse(error.message);
      return parsed?.customMessage || parsed?.message || error.message;
    } catch {
      return error.message;
    }
  }
  return fallback;
};

const DEFAULT_PRODUCT_KEY = ENABLED_PRODUCTS[0]?.key || "business_plus";

const BrokerApplicationNew = () => {
  const navigate = useNavigate();
  const brokerBasePath = useBrokerBasePath();
  const { showError } = useContext(DialogContext) || {};
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState({
    accountType: DEFAULT_PRODUCT_KEY,
    brokerReferenceId: "",
    email: "",
    Business_Name: "",
    firstName: "",
    middleName: "",
    lastName: "",
  });
  const errors = useMemo(() => validate(form), [form]);
  const hasErrors = Object.keys(errors).length > 0;

  const activeProduct = getBrokerProduct(form.accountType) || ENABLED_PRODUCTS[0];
  const activeFields = fieldsForProduct(activeProduct);

  const update = (key, value) => {
    setSubmitError("");
    setForm((current) => ({ ...current, [key]: value }));
  };

  const createApplication = async () => {
    setShowErrors(true);
    setSubmitError("");
    if (hasErrors || !activeProduct) {
      return;
    }

    setSaving(true);
    try {
      const payload = activeProduct.buildCreatePayload(form, { trimValue, normalizeEmail });
      const response = await BrokerService[activeProduct.service.create](payload);
      const id = response?.data?._id
        || response?.data?.application?._id
        || response?.data?.business?._id
        || response?.data?.individualLite?._id;
      if (!id) {
        throw new Error("Application draft was not returned by the server.");
      }
      navigate(buildBrokerPath(brokerBasePath, `/applications/${id}/edit`));
    } catch (error) {
      const message = getErrorMessage(error);
      setSubmitError(message);
      showError?.(error || message);
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    await createApplication();
  };

  return (
    <div className="broker-page broker-page--application-new">
      <div className="form-content-container broker-application-new-container">
        <div className="form-content-body with-shadow compact broker-application-new-body">
          <div className="form-content-body-header">
            <div className="heading">New Client Application</div>
          </div>
          <div className="form-content-body-content">
            {submitError ? (
              <BrokerAlert type="error" title="Application was not created" actionLabel="Try again" onAction={createApplication}>
                {submitError}
              </BrokerAlert>
            ) : null}
            <Form onSubmit={onSubmit} noValidate className="broker-form">
              <Row className="broker-draft-form-grid g-3">
                <Col xs={12}>
                  <FormGroup className="broker-field">
                    <Label for="account-type">Product</Label>
                    <div id="account-type" className="broker-product-select" role="group" aria-label="Available account products">
                      {ENABLED_PRODUCTS.map((product) => (
                        <button
                          key={product.key}
                          type="button"
                          className={`broker-product-option ${form.accountType === product.key ? "is-selected" : ""}`}
                          aria-pressed={form.accountType === product.key}
                          onClick={() => update("accountType", product.key)}
                        >
                          <span>{product.label}</span>
                          <small>Available now</small>
                        </button>
                      ))}
                    </div>
                    {showErrors && errors.accountType ? <div className="invalid-feedback d-block">{errors.accountType}</div> : null}
                  </FormGroup>
                </Col>
                {activeFields.map((field) => (
                  <Col xs={12} key={field.name}>
                    <FormGroup className="broker-field">
                      <Label for={`broker-field-${field.name}`}>
                        {field.label}{field.required ? <span className="broker-field__req"> *</span> : null}
                        {FIELD_HELP[field.name] ? <> <BrokerHelpTooltip content={FIELD_HELP[field.name]} /></> : null}
                      </Label>
                      <Input
                        id={`broker-field-${field.name}`}
                        type={field.type === "email" ? "email" : "text"}
                        value={form[field.name] || ""}
                        onChange={(event) => update(field.name, event.target.value)}
                        onBlur={(event) => update(field.name, valueForField({ [field.name]: event.target.value }, field))}
                        invalid={showErrors && Boolean(errors[field.name])}
                        required={field.required}
                      />
                      <FormFeedback>{errors[field.name]}</FormFeedback>
                    </FormGroup>
                  </Col>
                ))}
              </Row>
              <div className="d-flex justify-content-between align-items-center business-v2-step-actions broker-application-new-actions">
                <Button
                  type="button"
                  className="form-content-btn-outline"
                  onClick={() => navigate(buildBrokerPath(brokerBasePath, "/applications"))}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" className="form-content-btn" disabled={saving}>
                  {saving ? <FaSpinner className="broker-spin" /> : null} Create application
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerApplicationNew;
